'use client';
import Image from 'next/image';
import { useMemo } from 'react';

import { Link } from '@lib/navigation';
import { WRImage } from '@utils/api/server/wr/image';
import { WRGuide } from '@utils/api/server/wr/page';
import { getCloudflareImageLink } from '@utils/getters';
import { useUserStore } from '@utils/user';

interface SideSectionMediaGuideProps {
  section: WRGuide;
}

const GuideSection = ({ section }: SideSectionMediaGuideProps) => {
  const { isPremium } = useUserStore();

  const getProperImage = (section: WRGuide): WRImage | undefined => {
    return isPremium && section?.image?.urlPremium ? section?.image?.urlPremium : section?.image.url;
  };
  const img = useMemo(() => {
    let url;
    const imageData = getProperImage(section);
    const split = imageData?.url.split('uploads/');
    try {
      const prefix =
        process.env.NEXT_PUBLIC_ENV === 'production' ? 'http://cdn-trans.info/uploads/' : split?.[0] + 'uploads/';

      url = getCloudflareImageLink(`${prefix}${split?.[1]}`);
    } catch (e) {
      url = null;
    }

    return {
      url,
      alt: imageData?.alt,
    };
  }, [section, isPremium]);

  return (
    <>
      <div className="flex flex-shrink-0 flex-grow items-stretch gap-3 rounded-2xl p-2 shadow-equal shadow-premium-200 md:h-72 md:basis-1/2 md:p-3 xxl:absolute xxl:h-[calc(50%-12px)] dark:border dark:border-gray-300 dark:shadow-none">
        <div className="relative aspect-[4/7] max-h-full w-24 overflow-hidden rounded-2xl md:w-32 lg:w-44">
          <Image
            loading="lazy"
            src={img?.url}
            alt={img?.alt || ''}
            width={200}
            height={310}
            className="absolute top-1/2 -translate-y-1/2 rounded-2xl object-cover"
          />
        </div>
        <div className="flex-1 md:flex md:flex-col md:justify-between">
          <div className="text-premium-900 dark:text-white">
            <div className="mb-1 flex w-full items-stretch md:mb-3">
              {section.heading.left && (
                <div className="flex-1 whitespace-nowrap bg-premium-900 pl-3 pr-6 text-center text-sm font-extrabold uppercase text-white clip-path-cut-right md:text-lg md:tracking-[4px] lg:text-2xl">
                  {section.heading.left}
                </div>
              )}
              {section.heading.right && (
                <div className="-ml-[6px] flex-1 whitespace-nowrap bg-orange-500 pl-6 pr-3 text-center text-sm font-extrabold uppercase text-white clip-path-cut-left md:text-lg md:tracking-[4px] lg:text-2xl dark:bg-white dark:text-dark-700">
                  {section.heading.right}
                </div>
              )}
            </div>
            <h3 className="mb-2 px-1 text-sm font-bold md:px-3 md:text-lg lg:mb-6 lg:text-xl">{section?.title}</h3>

            <p className="mb-2 px-1 text-xs font-medium md:mb-0 md:px-3 lg:text-sm">{section?.description}</p>
          </div>
          {(section?.cta?.url || section?.cta?.urlPremium) && (
            <div className="px-1 md:mt-auto md:px-3">
              <Link
                href={(isPremium && section?.cta?.urlPremium ? section?.cta?.urlPremium : section?.cta?.url) || ''}
                target="_blank"
                className="inline-block rounded-full bg-orange-500 px-4 py-2 text-sm text-white transition-colors hover:bg-orange-700 hover:text-white lg:px-12 lg:py-3 lg:text-base"
              >
                {isPremium && section?.cta?.labelPremium ? section?.cta?.labelPremium : section?.cta?.label}
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GuideSection;
